<template>
  <div>
    <div class="t bold">Milestone years</div>
    <div class="f m-tx1">
      <div class="t">Year</div>
      <div class="t m-lx6">{{ capitalize(pointsNoun) }}</div>
    </div>
    <div v-for="(milestone, idx) in milestoneYears" :key="idx" class="f m-t">
      <div class="w-x8 m-rx1">
        <donut-input-async-number :model-value="milestone.year" class="full-width" @submit="(val, onComplete) => onMilestoneYearSubmit(milestone, val, onComplete)" />
      </div>
      <div class="w-x11">
        <donut-input-async-number :model-value="milestone.points" class="full-width" @submit="(val, onComplete) => onMilestonePointsSubmit(milestone, val, onComplete)" />
      </div>
      <div class="t w-x13 m-lx1">
        <span v-emoji="`:${pointsConfig.emoji}:`" />
        <span> = ${{ (milestone.points * state.pointsConfig.dollarsPerPoint).toFixed(2) }}</span>
      </div>
      <lucide-icon v-if="milestoneYears.length >= 2" name="Trash" class="purple c-p" @click="deleteMilestone(milestone)" />
    </div>
    <donut-button :class="`${milestoneYears.length >= 99 ? 'disabled ' : ''}m-tx2`" @click="addYear">Add year</donut-button>
  </div>
</template>

<script setup>
  import { inject, computed } from 'vue';
  import { useStore } from 'vuex';
  import { capitalize, cloneDeep } from 'lodash';

  const $i18n = inject('$i18n');
  const { state, dispatch } = useStore();

  const pointsNoun = computed(() => $i18n.t('shoutouts.point', { count: 2 }));
  const pointsConfig = computed(() => state.pointsConfig.config);
  const config = computed(() => state.bonusMilestoneConfigs.anniversary);

  const milestoneYears = computed(() => Object.entries(config.value.points_by_year || {}).map(([year, points]) => ({ year: parseInt(year), points })));

  const onMilestoneYearSubmit = (milestone, year, onComplete) => {
    const oldYear = milestone.year;
    // Range is 1 - 99 years, only integers, and all unique values
    if (year < 1 || year > 99 || parseInt(year) !== year || milestoneYears.value.some(m => parseInt(m.year) === year && m !== milestone)) {
      onComplete({ value: oldYear, icon: 'error' });
    } else {
      const pby = cloneDeep(config.value.points_by_year);
      pby[year] = pby[oldYear];
      delete pby[oldYear];
      dispatch('bonusMilestoneConfigs/updateMilestoneConfig', { type: 'anniversary', params: { points_by_year: pby } })
        .then(() => onComplete({ icon: 'success' }))
        .catch(() => onComplete({ value: oldYear, icon: 'error' }));
    }
  };

  const onMilestonePointsSubmit = (milestone, points, onComplete) => {
    const oldPoints = milestone.points;
    // Range is 1 - 99,999 points, only integers
    if (points < 1 || points > 99999 || parseInt(points) !== points) {
      onComplete({ value: oldPoints, icon: 'error' });
    } else {
      const pby = cloneDeep(config.value.points_by_year);
      pby[milestone.year] = points;
      dispatch('bonusMilestoneConfigs/updateMilestoneConfig', { type: 'anniversary', params: { points_by_year: pby } })
        .then(() => onComplete({ icon: 'success' }))
        .catch(() => onComplete({ value: oldPoints, icon: 'error' }));
    }
  };

  const deleteMilestone = (milestone) => {
    if (milestoneYears.value.length < 2) return;

    const pby = cloneDeep(config.value.points_by_year);
    delete pby[milestone.year];
    dispatch('bonusMilestoneConfigs/updateMilestoneConfig', { type: 'anniversary', params: { points_by_year: pby } });
  };

  const addYear = () => {
    if (milestoneYears.value.length >= 99) return;

    const pby = cloneDeep(config.value.points_by_year);
    const existingYears = Object.keys(pby).map(y => parseInt(y));
    const maxYear = Math.max(...existingYears);
    let newYear = maxYear + 1;
    while (newYear > 99 || existingYears.includes(newYear)) newYear -= 1;
    pby[newYear] = 10;
    dispatch('bonusMilestoneConfigs/updateMilestoneConfig', { type: 'anniversary', params: { points_by_year: pby } });
  };
</script>
